import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  TablePagination
} from '@mui/material';
import {
  Edit,
  Delete,
  ContentCopy,
  Print,
  Add as AddIcon,
  Search,
  FilterList,
  MoreVert,
  ViewColumn,
  Close
} from '@mui/icons-material';
import { quoteService } from '../services/quoteService';
import { format } from 'date-fns';
import { formatCurrency } from '../components/CurrencyTextField';
import { formatDateOnly, parseApiTimestamp } from '../utils/dateUtils';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { printWithFilename } from '../utils/printUtils';

const quoteStyleShortLabel = (style) => {
  if (style === 'scope_of_work') return 'Field';
  return 'Std';
};

const Quotes = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerFilter = searchParams.get('customer');
  const partFilter = searchParams.get('part_id');
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedQuoteForHeader, setSelectedQuoteForHeader] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [quoteToConvert, setQuoteToConvert] = useState(null);
  const [converting, setConverting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const clickTimeoutRef = useRef(null);
  const [tableSearch, setTableSearch] = useState('');
  const [debouncedTableSearch, setDebouncedTableSearch] = useState('');
  const [colDateFrom, setColDateFrom] = useState('');
  const [colDateTo, setColDateTo] = useState('');
  const [sortKey, setSortKey] = useState('quote_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [quoteStyleFilter, setQuoteStyleFilter] = useState('');
  const [quotesTotal, setQuotesTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const DEFAULT_QUOTE_COL_WIDTHS = {
    quote: 200,
    date: 110,
    order: 190,
    time: 90,
    customerNumber: 100,
    customer: 240,
    total: 130,
    quotedBy: 150,
    status: 120,
    description: 280,
    actions: 120
  };
  const {
    colWidths,
    setColWidths,
    startResize,
    saveColWidths,
    resetColWidths
  } = useResizableColumns({
    defaultWidths: DEFAULT_QUOTE_COL_WIDTHS,
    storageKey: 'wm_quotes_col_widths',
    minWidths: {
      quote: 120,
      date: 90,
      order: 130,
      time: 80,
      customerNumber: 90,
      customer: 160,
      total: 110,
      quotedBy: 120,
      status: 100,
      description: 160,
      actions: 100
    }
  });
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionDialogTitle, setRevisionDialogTitle] = useState('Revision History');
  const [revisionDialogItems, setRevisionDialogItems] = useState([]);
  const [revisionDialogLoading, setRevisionDialogLoading] = useState(false);

  const handleSort = (key) => {
    setSortDirection((prevDir) => (sortKey === key ? (prevDir === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const filteredQuotes = useMemo(() => {
    let list = quotes.filter((q) => String(q?.status || '').toLowerCase() !== 'deprecated');
    if (colDateFrom) {
      const fromPart = String(colDateFrom).trim().split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(fromPart)) list = list.filter((q) => q.quote_date && (String(q.quote_date).trim().split('T')[0] >= fromPart));
    }
    if (colDateTo) {
      const toPart = String(colDateTo).trim().split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(toPart)) list = list.filter((q) => q.quote_date && (String(q.quote_date).trim().split('T')[0] <= toPart));
    }
    const getSortValue = (q) => {
      switch (sortKey) {
        case 'identifier':
          return String(q.identifier ?? '');
        case 'quote_date':
          return q.quote_date
            ? (parseApiTimestamp(q.quote_date)?.getTime() ?? 0)
            : (parseApiTimestamp(q.created_at)?.getTime() ?? 0);
        case 'work_order':
          return String(q.work_order_identifier ?? q.work_order_number ?? '');
        case 'time':
          return parseApiTimestamp(q.created_at)?.getTime() ?? 0;
        case 'customer_number':
          return String(q.customer_number ?? '');
        case 'customer_name':
          return String((q.company_name || [q.customer_first_name, q.customer_last_name].filter(Boolean).join(' ') || '')).toLowerCase();
        case 'total_amount':
          return q.total_amount != null ? Number(q.total_amount) : 0;
        case 'quoted_by':
          return String(`${q.quoted_by_first_name || ''} ${q.quoted_by_last_name || ''}`.trim() || q.quoted_by || '').toLowerCase();
        case 'status':
          return String(q.status ?? '').toLowerCase();
        case 'description':
          return String(q.description ?? '').toLowerCase();
        default:
          return '';
      }
    };

    const dir = sortDirection === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);

      if (typeof va === 'number' && typeof vb === 'number') {
        return dir * (va - vb);
      }

      return dir * String(va ?? '').localeCompare(String(vb ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    });

    return list;
  }, [quotes, colDateFrom, colDateTo, sortKey, sortDirection]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTableSearch(tableSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [tableSearch]);

  const quotesFilterKeyRef = useRef(null);

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    };
  }, []);

  const loadQuotes = useCallback(async () => {
    const filterKey = [statusFilter, customerFilter || '', partFilter || '', quoteStyleFilter, debouncedTableSearch].join(
      '|'
    );
    const filterJustChanged =
      quotesFilterKeyRef.current != null && quotesFilterKeyRef.current !== filterKey;
    quotesFilterKeyRef.current = filterKey;

    const effectivePage = filterJustChanged ? 0 : page;
    if (filterJustChanged && page !== 0) {
      setPage(0);
    }

    try {
      setLoading(true);
      setError('');
      const params = {
        limit: rowsPerPage,
        offset: effectivePage * rowsPerPage,
        search: debouncedTableSearch,
        status: statusFilter || undefined,
        ...(customerFilter ? { customerId: customerFilter } : {}),
        ...(partFilter ? { part_id: partFilter } : {}),
        ...(quoteStyleFilter ? { quote_style: quoteStyleFilter } : {})
      };
      const response = await quoteService.getAll(params);
      const quotesList = Array.isArray(response?.data) ? response.data : (response?.data?.data ?? []);
      const total = typeof response?.total === 'number' ? response.total : quotesList.length;
      const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      if (effectivePage > maxPage) {
        setPage(maxPage);
        return;
      }
      setQuotesTotal(total);
      setQuotes(quotesList);
      setSelectedQuoteForHeader(quotesList.length > 0 ? quotesList[0] : null);
    } catch (err) {
      console.error('Error loading quotes:', err);
      setError(err.response?.data?.message || 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, customerFilter, partFilter, quoteStyleFilter, debouncedTableSearch, page, rowsPerPage]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  const handleQuoteDoubleClick = (quoteId) => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    navigate(`/quotes/${quoteId}`);
  };

  const handleQuoteRowClick = (quote) => {
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null;
      setSelectedQuoteForHeader(quote);
    }, 200);
  };

  const handleQuoteClick = (quoteId) => {
    navigate(`/quotes/${quoteId}`);
  };

  const handleMenuOpen = (event, quote) => {
    event.stopPropagation();
    setSelectedQuote(quote);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedQuote(null);
  };

  const handleDeleteClick = (event, quote) => {
    event.stopPropagation();
    setQuoteToDelete(quote);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
    }
  };

  const confirmDeleteQuote = async () => {
    if (!quoteToDelete) return;
    try {
      setDeleting(true);
      setError('');
      await quoteService.delete(quoteToDelete.id);
      handleDeleteDialogClose();
      setSelectedQuoteForHeader(null);
      await loadQuotes();
    } catch (err) {
      console.error('Error deleting quote:', err);
      setError(err.response?.data?.message || 'Failed to delete quote');
    } finally {
      setDeleting(false);
    }
  };

  const handleConvertClick = (event, quote) => {
    event.stopPropagation();
    setQuoteToConvert(quote);
    setConvertDialogOpen(true);
  };

  const handleConvertClose = () => {
    setConvertDialogOpen(false);
    setQuoteToConvert(null);
  };

  const confirmConvertToOrder = async () => {
    if (!quoteToConvert) return;

    try {
      setConverting(true);
      setError('');
      const response = await quoteService.convertToOrder(quoteToConvert.id);
      
      if (response.success) {
        handleConvertClose();
        // Refresh quotes list
        await loadQuotes();
        // Navigate to the new order
        navigate(`/work-orders/${response.data.id}`);
      } else {
        setError(response.message || 'Failed to convert quote');
        handleConvertClose();
      }
    } catch (err) {
      console.error('Error converting quote:', err);
      setError(err.response?.data?.message || 'Failed to convert quote');
      handleConvertClose();
    } finally {
      setConverting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      open: 'info',
      won: 'success',
      lost: 'error',
      expired: 'warning',
      deprecated: 'warning'
    };
    return colors[status] || 'default';
  };

  const displayStatusLabel = (status) => {
    const normalized = String(status || 'draft').toLowerCase();
    return normalized === 'deprecated' ? 'revised' : normalized;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatDateOnly(dateString, 'MM/dd/yyyy') || dateString;
  };

  const formatQuoteDate = (quote) => {
    const dt = quote.quote_date || quote.created_at;
    if (!dt) return '—';
    const formatted = quote.quote_date
      ? formatDateOnly(dt, 'EEE M/d')
      : (() => {
          const parsed = parseApiTimestamp(dt);
          return parsed ? format(parsed, 'EEE M/d') : '';
        })();
    return formatted || '—';
  };

  const formatQuoteTime = (quote) => {
    const dt = quote.created_at;
    if (!dt) return '—';
    try {
      const parsed = parseApiTimestamp(dt);
      return parsed ? format(parsed, 'h:mma') : '—';
    } catch {
      return '—';
    }
  };

  const formatQuoteNumber = (q) => {
    if (!q || !q.identifier) return '—';
    return q.identifier;
  };

  const handleOpenRevisionDialog = async (quote) => {
    setRevisionDialogTitle(`Revision History: ${quote.identifier}`);
    setRevisionDialogItems([]);
    setRevisionDialogLoading(true);
    setRevisionDialogOpen(true);
    try {
      const res = await quoteService.getRevisions(quote.id);
      setRevisionDialogItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load revision history:', err);
      setRevisionDialogItems([]);
    } finally {
      setRevisionDialogLoading(false);
    }
  };

  const handlePrint = () => printWithFilename('Quotes');
  const handlePrintQuote = (quote) => {
    if (!quote?.id) return;
    navigate(`/quotes/${quote.id}?print=1`);
  };

  /** Match Orders list (`WorkOrders`) — tighter than MUI `size="small"` alone. */
  const quotesTableDenseSx = {
    '& .MuiTableCell-root': {
      py: 0.35,
      px: '8px',
      fontSize: '0.75rem',
      lineHeight: 1.2,
      verticalAlign: 'middle'
    },
    '& .MuiTableCell-head': {
      py: 0.45,
      fontSize: '0.7rem'
    },
    '& .MuiTableCell-head .MuiBox-root': { pr: 1 },
    '& .MuiChip-root': {
      height: 20,
      maxWidth: '100%',
      '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', lineHeight: 1.1 }
    },
    '& .MuiIconButton-root': { p: 0.25 },
    '& .MuiButton-sizeSmall': {
      py: 0.125,
      px: 0.75,
      fontSize: '0.75rem',
      lineHeight: 1.2,
      minHeight: 24
    }
  };

  return (
    <Box>
      <Box className="no-print-quotes">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Quotes
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/quotes/new')}
            sx={{ mr: 0.5 }}
          >
            New
          </Button>
          <IconButton size="small" color="primary" title="Edit">
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            title="Delete"
            onClick={() => {
              if (!selectedQuoteForHeader) return;
              setQuoteToDelete(selectedQuoteForHeader);
              setDeleteDialogOpen(true);
            }}
            disabled={!selectedQuoteForHeader}
          >
            <Delete fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Copy">
            <ContentCopy fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Print" onClick={(e) => setPrintMenuAnchor(e.currentTarget)}>
            <Print fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Column widths" onClick={() => setColumnsDialogOpen(true)}>
            <ViewColumn fontSize="small" />
          </IconButton>
          <Menu anchorEl={printMenuAnchor} open={!!printMenuAnchor} onClose={() => setPrintMenuAnchor(null)} disableEnforceFocus disableRestoreFocus>
            <MenuItem onClick={() => { handlePrint(); setPrintMenuAnchor(null); }}>Print list</MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedQuoteForHeader?.id) navigate(`/quotes/${selectedQuoteForHeader.id}`);
                setPrintMenuAnchor(null);
              }}
              disabled={!selectedQuoteForHeader}
            >
              Reprint selected
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Quote Information Bar */}
        <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quote #</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatQuoteNumber(selectedQuoteForHeader)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Date</Typography>
              <Typography variant="body2">{selectedQuoteForHeader ? formatQuoteDate(selectedQuoteForHeader) : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
              <Typography variant="body2">
                {selectedQuoteForHeader?.po_number || selectedQuoteForHeader?.work_order_po_number || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order</Typography>
              <Typography variant="body2" component="span">
                {selectedQuoteForHeader?.work_order_id != null ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/work-orders/${selectedQuoteForHeader.work_order_id}`);
                    }}
                    sx={{ textTransform: 'none' }}
                    title="Go to order"
                  >
                    {selectedQuoteForHeader.work_order_identifier ?? selectedQuoteForHeader.work_order_number ?? selectedQuoteForHeader.work_order_id}
                  </Button>
                ) : (
                  '—'
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
              <Typography variant="body2">
                {selectedQuoteForHeader?.customer_id ? (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                    onClick={() => navigate(`/customers/${selectedQuoteForHeader.customer_id}`)}
                  >
                    {selectedQuoteForHeader.company_name || [selectedQuoteForHeader.customer_first_name, selectedQuoteForHeader.customer_last_name].filter(Boolean).join(' ') || `#${selectedQuoteForHeader.customer_id}`}
                  </Button>
                ) : (
                  selectedQuoteForHeader
                    ? (selectedQuoteForHeader.company_name || [selectedQuoteForHeader.customer_first_name, selectedQuoteForHeader.customer_last_name].filter(Boolean).join(' ') || '—')
                    : '—'
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quoted By</Typography>
              <Typography variant="body2">
                {selectedQuoteForHeader?.quoted_by_first_name && selectedQuoteForHeader?.quoted_by_last_name
                  ? `${selectedQuoteForHeader.quoted_by_first_name} ${selectedQuoteForHeader.quoted_by_last_name}`
                  : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Total</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedQuoteForHeader ? formatCurrency(selectedQuoteForHeader.total_amount, '—') : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
              <Chip
                label={displayStatusLabel(selectedQuoteForHeader?.status ?? '—')}
                color={getStatusColor(selectedQuoteForHeader?.status)}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Due Date</Typography>
              <Typography variant="body2">
                {selectedQuoteForHeader?.expires_on ? formatDate(selectedQuoteForHeader.expires_on) : '—'}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 200 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Description</Typography>
              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedQuoteForHeader?.description || '—'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setSelectedQuoteForHeader(null)} disabled={!selectedQuoteForHeader} sx={{ ml: 'auto' }} title="Clear selection">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<FilterList />}
            size="small"
          >
            Search Options
          </Button>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {quotesTotal === 0
              ? 'No quotations match the current filters.'
              : quotes.length === 0
                ? `Page ${page + 1} · ${quotesTotal} quotation${quotesTotal === 1 ? '' : 's'} total`
                : `Showing ${page * rowsPerPage + 1}–${page * rowsPerPage + quotes.length} of ${quotesTotal} quotation${quotesTotal === 1 ? '' : 's'}`}
            {customerFilter ? ' · filtered by customer' : ''}
            {partFilter ? ' · filtered by part' : ''}
            {debouncedTableSearch ? ' · table filtered by search (all quotes)' : ''}
          </Typography>
          <Button variant="outlined" size="small" startIcon={<FilterList />}>
            Recent
          </Button>
          <Button variant="outlined" size="small" startIcon={<Print />}>
            Reprint
          </Button>
        </Box>
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280, maxWidth: 500 }}>
            <TextField
              size="small"
              fullWidth
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search all quotes — ID, customer, PO, notes, status, amount, date, quoted by…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: tableSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setTableSearch('')}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewColumn />}
            onClick={() => setColumnsDialogOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Columns
          </Button>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="quote-style-filter-label">Quote style</InputLabel>
            <Select
              labelId="quote-style-filter-label"
              label="Quote style"
              value={quoteStyleFilter}
              onChange={(e) => setQuoteStyleFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="scope_of_work">Field Work / Service</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Filter by date:
            </Typography>
            <TextField
              size="small"
              type="date"
              placeholder="From"
              value={colDateFrom}
              onChange={(e) => setColDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150, '& .MuiInputBase-input': { minWidth: 130 } }}
              inputProps={{ 'aria-label': 'Date from' }}
            />
            <Typography variant="body2" color="text.secondary">to</Typography>
            <TextField
              size="small"
              type="date"
              placeholder="To"
              value={colDateTo}
              onChange={(e) => setColDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150, '& .MuiInputBase-input': { minWidth: 130 } }}
              inputProps={{ 'aria-label': 'Date to' }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Quotes Table */}
      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader sx={quotesTableDenseSx}>
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.quote} onResizeStart={(e) => startResize('quote', e)}>
                <TableSortLabel active={sortKey === 'identifier'} direction={sortDirection} onClick={() => handleSort('identifier')}>
                  Quote
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.date} onResizeStart={(e) => startResize('date', e)}>
                <TableSortLabel active={sortKey === 'quote_date'} direction={sortDirection} onClick={() => handleSort('quote_date')}>
                  Date
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.order} onResizeStart={(e) => startResize('order', e)}>
                <TableSortLabel active={sortKey === 'work_order'} direction={sortDirection} onClick={() => handleSort('work_order')}>
                  Order #
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.time} onResizeStart={(e) => startResize('time', e)}>
                <TableSortLabel active={sortKey === 'time'} direction={sortDirection} onClick={() => handleSort('time')}>
                  Time
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.customerNumber} onResizeStart={(e) => startResize('customerNumber', e)}>
                <TableSortLabel active={sortKey === 'customer_number'} direction={sortDirection} onClick={() => handleSort('customer_number')}>
                  Cust #
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.customer} onResizeStart={(e) => startResize('customer', e)}>
                <TableSortLabel active={sortKey === 'customer_name'} direction={sortDirection} onClick={() => handleSort('customer_name')}>
                  Customer
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.total} onResizeStart={(e) => startResize('total', e)}>
                <TableSortLabel active={sortKey === 'total_amount'} direction={sortDirection} onClick={() => handleSort('total_amount')}>
                  Quote Total
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.quotedBy} onResizeStart={(e) => startResize('quotedBy', e)}>
                <TableSortLabel active={sortKey === 'quoted_by'} direction={sortDirection} onClick={() => handleSort('quoted_by')}>
                  Quoted By
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.status} onResizeStart={(e) => startResize('status', e)}>
                <TableSortLabel active={sortKey === 'status'} direction={sortDirection} onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.description} onResizeStart={(e) => startResize('description', e)}>
                <TableSortLabel active={sortKey === 'description'} direction={sortDirection} onClick={() => handleSort('description')}>
                  Description
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)}>Actions</ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredQuotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  {quotes.length === 0 ? 'No quotes found. Click "New" to create your first quote.' : 'No quotes match the search.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredQuotes.map((quote) => (
                <TableRow
                  key={quote.id}
                  hover
                  onClick={() => handleQuoteRowClick(quote)}
                  onDoubleClick={() => handleQuoteDoubleClick(quote.id)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: selectedQuoteForHeader?.id === quote.id ? 'action.selected' : 'inherit'
                  }}
                >
                  <TableCell sx={{ width: colWidths.quote }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      {formatQuoteNumber(quote)}
                      <Chip
                        label={quoteStyleShortLabel(quote.quote_style)}
                        size="small"
                        variant={quote.quote_style === 'scope_of_work' ? 'filled' : 'outlined'}
                        color={quote.quote_style === 'scope_of_work' ? 'default' : undefined}
                        sx={
                          quote.quote_style === 'scope_of_work'
                            ? { textTransform: 'capitalize' }
                            : undefined
                        }
                        title={
                          quote.quote_style === 'scope_of_work'
                            ? 'Field Work / Service'
                            : 'Standard'
                        }
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: colWidths.date }}>{formatQuoteDate(quote)}</TableCell>
                  <TableCell sx={{ width: colWidths.order }}>
                    {quote.work_order_id != null ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/work-orders/${quote.work_order_id}`);
                        }}
                        sx={{ textTransform: 'none' }}
                        title="Go to order"
                      >
                        {quote.work_order_identifier ?? quote.work_order_number ?? quote.work_order_id}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.time }}>{formatQuoteTime(quote)}</TableCell>
                  <TableCell sx={{ width: colWidths.customerNumber }}>{quote.customer_number ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.customer }}>
                    {quote.company_name || [quote.customer_first_name, quote.customer_last_name].filter(Boolean).join(' ') || '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.total }}>
                    {formatCurrency(quote.total_amount, '-')}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.quotedBy }}>
                    {quote.quoted_by_first_name && quote.quoted_by_last_name
                      ? `${quote.quoted_by_first_name} ${quote.quoted_by_last_name}`
                      : quote.quoted_by || '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.status }}>
                    <Chip
                      label={displayStatusLabel(quote.status || 'draft')}
                      size="small"
                      color={getStatusColor(quote.status)}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: colWidths.description }}>
                    {quote.description
                      ? (quote.description.length > 50 ? quote.description.substring(0, 50) + '...' : quote.description)
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.actions }} onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Print quote"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrintQuote(quote);
                        }}
                      >
                        <Print fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        title={
                          quote.work_order_id != null
                            ? 'Already has order'
                            : quote.status === 'won'
                              ? 'Quote already won'
                              : 'Convert to order'
                        }
                        onClick={(e) => {
                          if (quote.work_order_id != null || quote.status === 'won') return;
                          handleConvertClick(e, quote);
                        }}
                        disabled={quote.work_order_id != null || quote.status === 'won'}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, quote)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[50, 100]}
          count={quotesTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        disableEnforceFocus
        disableRestoreFocus
      >
        <MenuItem onClick={() => {
          if (selectedQuote) handleQuoteClick(selectedQuote.id);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        {selectedQuote?.work_order_id != null && (
          <MenuItem onClick={() => {
            if (selectedQuote) navigate(`/work-orders/${selectedQuote.work_order_id}`);
            handleMenuClose();
          }}>
            View order {selectedQuote?.work_order_identifier ?? selectedQuote?.work_order_number ?? selectedQuote?.work_order_id}
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          if (selectedQuote) handleQuoteClick(selectedQuote.id);
          handleMenuClose();
        }}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>Copy</MenuItem>
        <MenuItem onClick={() => { handlePrint(); handleMenuClose(); }}>Print</MenuItem>
        <MenuItem
          onClick={(e) => selectedQuote && handleDeleteClick(e, selectedQuote)}
          sx={{ color: 'error.main' }}
          disabled={selectedQuote?.work_order_id != null}
        >
          Delete
        </MenuItem>
      </Menu>

      </Box>

      {/* Print view – quotations list */}
      <PrintPage title="Quotations" showSpacer={false}>
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          {filteredQuotes.length} record(s). Printed {format(new Date(), 'MM/dd/yyyy HH:mm')}
        </p>
        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: 6, textAlign: 'left' }}>Quote #</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Date</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Order #</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Total</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuotes.map((q) => (
              <tr key={q.id}>
                <td style={{ padding: 6 }}>{formatQuoteNumber(q)}</td>
                <td style={{ padding: 6 }}>{formatDate(q.quote_date)}</td>
                <td style={{ padding: 6 }}>{q.work_order_identifier ?? q.work_order_number ?? (q.work_order_id != null ? String(q.work_order_id) : '—')}</td>
                <td style={{ padding: 6 }}>{q.company_name || [q.customer_first_name, q.customer_last_name].filter(Boolean).join(' ') || q.customer_number || '-'}</td>
                <td style={{ padding: 6 }}>{formatCurrency(q.total_amount, '-')}</td>
                <td style={{ padding: 6 }}>{displayStatusLabel(q.status || '-')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PrintPage>

      {/* Column Width Settings Dialog */}
      <Dialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        aria-labelledby="columns-width-dialog-title"
      >
        <DialogTitle id="columns-width-dialog-title">Column widths</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Adjust the column widths for the Quotes table.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Object.keys(DEFAULT_QUOTE_COL_WIDTHS).map((key) => (
              <TextField
                key={key}
                label={`${key} column (px)`}
                type="number"
                size="small"
                value={colWidths[key]}
                onChange={(e) => setColWidths((prev) => ({ ...prev, [key]: Number(e.target.value) || 0 }))}
                inputProps={{ min: 80, step: 10 }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetColWidths} color="inherit">
            Reset
          </Button>
          <Button onClick={() => setColumnsDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              saveColWidths(colWidths);
              setColumnsDialogOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Quote Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-quote-dialog-title"
        aria-describedby="delete-quote-dialog-description"
      >
        <DialogTitle id="delete-quote-dialog-title">
          Delete Quote?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-quote-dialog-description">
            {quoteToDelete?.work_order_id != null
              ? `Quote ${quoteToDelete ? formatQuoteNumber(quoteToDelete) : ''} has an associated order and cannot be deleted.`
              : `Are you sure you want to delete quote ${quoteToDelete ? formatQuoteNumber(quoteToDelete) : ''}? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteQuote}
            color="error"
            variant="contained"
            autoFocus
            disabled={deleting || quoteToDelete?.work_order_id != null}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revision History Dialog */}
      <Dialog
        open={revisionDialogOpen}
        onClose={() => setRevisionDialogOpen(false)}
        aria-labelledby="revision-history-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="revision-history-dialog-title">{revisionDialogTitle}</DialogTitle>
        <DialogContent>
          {revisionDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : revisionDialogItems.length === 0 ? (
            <DialogContentText>No previous revisions found.</DialogContentText>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quote #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {revisionDialogItems.map((rev) => (
                  <TableRow key={rev.id}>
                    <TableCell>{rev.identifier}</TableCell>
                    <TableCell>{rev.quote_date ? formatDate(rev.quote_date) : '—'}</TableCell>
                    <TableCell>
                      <Chip label={displayStatusLabel(rev.status || 'draft')} size="small" color={getStatusColor(rev.status)} sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>{formatCurrency(rev.total_amount, '—')}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => { setRevisionDialogOpen(false); navigate(`/quotes/${rev.id}`); }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Order Confirmation Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={handleConvertClose}
        aria-labelledby="convert-order-dialog-title"
        aria-describedby="convert-order-dialog-description"
      >
        <DialogTitle id="convert-order-dialog-title">
          Create order from quote?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="convert-order-dialog-description">
            Create an order from Quote #{quoteToConvert ? formatQuoteNumber(quoteToConvert) : ''}? A new order will be linked to this quote; the quote status is not changed automatically.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConvertClose} color="primary" disabled={converting}>
            Cancel
          </Button>
          <Button 
            onClick={confirmConvertToOrder} 
            color="primary" 
            variant="contained" 
            autoFocus 
            disabled={converting}
          >
            {converting ? 'Creating…' : 'Create order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Quotes;
